import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import senpiperLogo from "../assets/Senpiper Logo_Dark BG.svg";
import ContactModal from "./ContactModal"; 

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isLowCodeOpen, setIsLowCodeOpen] = useState(false);
  const [isIndustriesOpen, setIsIndustriesOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    lowCode: false,
    industries: false
  });
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleExpandedMenu = (menu: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu as keyof typeof prev]
    }));
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest("nav")) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isMenuOpen]);

  const getNavClass = (path: string) =>
    `px-3 py-1 inline-flex justify-center items-center ${
      location.pathname === path
        ? "border-b-2 border-blue-900 text-blue-900"
        : "text-gray-700 hover:text-blue-900"
    } text-sm sm:text-base lg:text-lg xl:text-xl font-medium font-['Nunito Sans'] leading-relaxed tracking-tight`;

  return (
    <>
      <nav className="w-full bg-white shadow-md relative z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center h-16 sm:h-20 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/" className="block">
              <img
                src={senpiperLogo}
                alt="Senpiper"
                className="h-6 w-auto sm:h-8 lg:h-10"
              />
            </a>
          </div>
          
          {/* Center Menu (Desktop) */}
          <div className="hidden sm:flex md:gap-0.5 lg:gap-2 xl:gap-6">
            <a
              href="https://senpiper.com/"
              target="_blank"
              rel="noopener noreferrer"
              className={getNavClass("")} 
            >
              Home
            </a>
            
            {/* Low-code Platform Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setIsLowCodeOpen(true)}
              onMouseLeave={() => setIsLowCodeOpen(false)}
            >
              <div className={getNavClass("")}>
                Low-code Platform
              </div>
              
              {/* Dropdown Content */}
              {isLowCodeOpen && (
                <div className="absolute top-full -left-16 bg-white text-gray-800 group-hover:visible w-[280px] sm:w-[450px] lg:w-[605px] p-4 sm:p-6 rounded-lg shadow-xl border border-gray-200">
                  {/* Low Code Section */}
                  <div className="w-full pb-4 sm:pb-6">
                    <div className="flex justify-between">
                      <div className="space-y-1">
                        <div className="flex gap-2 items-center">
                          <div className="w-6 sm:w-8 flex items-center">
                            <img
                              src="https://res.cloudinary.com/senpiper/image/upload/v1680631485/senpiper_assets/new_website_assets/Flexible_Integration_icon_h9v77v.svg"
                              alt="Low-Code"
                              className="w-6 sm:w-8"
                            />
                          </div>
                          <p className="font-semibold text-sm sm:text-base lg:text-lg">
                            Low-Code
                          </p>
                        </div>
                        <ul className="text-gray-600 text-xs sm:text-sm lg:text-base space-y-1 ml-8 sm:ml-10">
                          <li className="hover:text-yellow-500 cursor-pointer">
                            <a href="https://senpiper.com/platform/">Our Platform</a>
                          </li>
                          <li className="hover:text-yellow-500 cursor-pointer">
                            <a href="https://senpiper.com/products/low-code/low-code//">What is Low-code?</a>
                          </li>
                          <li className="hover:text-yellow-500 cursor-pointer">
                            <a href="https://senpiper.com/products/low-code/comparison/">Low-code comparison</a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  {/* Features Section */}
                  <div>
                    <div className="w-full px-3 sm:px-4 py-1 bg-gray-100 rounded inline-flex justify-start items-center gap-2.5">
                      <div className="text-gray-700 text-sm sm:text-base lg:text-lg font-normal">Features</div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row pt-3 sm:pt-4">
                      {/* Left Column */}
                      <ul className="space-y-1 sm:pr-4 lg:pr-8 sm:border-r-2 border-gray-200">
                        {/* Data Capture */}
                        <li>
                          <div className="space-y-1">
                            <div className="flex gap-2 items-center">
                              <div className="w-6 sm:w-8 flex items-center">
                                <img
                                  src="https://res.cloudinary.com/senpiper/image/upload/v1666804563/senpiper_assets/new_website_assets/data_capture_uz7hxc.svg"
                                  alt="Data Capture"
                                  className="w-6 sm:w-8"
                                />
                              </div>
                              <p className="font-semibold text-sm sm:text-base lg:text-lg">
                                Data Capture
                              </p>
                            </div>
                            <ul className="text-gray-600 text-xs sm:text-sm lg:text-base space-y-1 sm:space-y-2 ml-8 sm:ml-12">
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/data-capture/data-collection/">Data Collection</a>
                              </li>
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/data-capture/form-field/">Form Fields</a>
                              </li>
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/data-capture/schema-relationship/">Schema Relation</a>
                              </li>
                            </ul>
                          </div>
                        </li>
                        
                        {/* Automation */}
                        <li>
                          <div className="space-y-1 sm:space-y-2">
                            <div className="flex gap-2 sm:gap-4 items-center">
                              <div className="w-6 sm:w-8 flex items-center">
                                <img
                                  src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/Automation_w3lto6.svg"
                                  alt="Automation"
                                  className="w-6 sm:w-8"
                                />
                              </div>
                              <p className="font-semibold text-sm sm:text-base lg:text-lg">
                                Automation
                              </p>
                            </div>
                            <ul className="text-gray-600 text-xs sm:text-sm lg:text-base space-y-1 sm:space-y-2 ml-8 sm:ml-12">
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/automation/workflow/">Workflow</a>
                              </li>
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/automation/scheduler/">Scheduler</a>
                              </li>
                            </ul>
                          </div>
                        </li>
                        
                        {/* Communication */}
                        <li>
                          <a href="https://senpiper.com//features/communication/" className="flex gap-2 sm:gap-4 items-center hover:text-yellow-500">
                            <div className="w-6 sm:w-8 flex items-center">
                              <img
                                src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/Chat_lflyjy.svg"
                                alt="Communication"
                                className="w-6 sm:w-8"
                              />
                            </div>
                            <p className="font-semibold text-sm sm:text-base lg:text-lg">
                              Communication
                            </p>
                          </a>
                        </li>
                        
                        {/* Security */}
                        <li>
                          <a href="https://senpiper.com//features/security/" className="flex gap-2 sm:gap-4 items-center hover:text-yellow-500">
                            <div className="w-6 sm:w-8 flex items-center">
                              <img
                                src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/security_gakizz.svg"
                                alt="Security"
                                className="w-6 sm:w-8"
                              />
                            </div>
                            <p className="font-semibold text-sm sm:text-base lg:text-lg">
                              Security
                            </p>
                          </a>
                        </li>
                      </ul>
                      
                      {/* Right Column */}
                      <ul className="space-y-3 sm:space-y-6 sm:pl-4 lg:pl-6 mt-3 sm:mt-0">
                        {/* Smart Dashboard */}
                        <li>
                          <a href="https://senpiper.com//features/analytics/" className="flex gap-2 sm:gap-4 items-center hover:text-yellow-500">
                            <div className="w-6 sm:w-8 flex items-center">
                              <img
                                src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/analytis_irmqhy.svg"
                                alt="Smart Dashboard"
                                className="w-6 sm:w-8"
                              />
                            </div>
                            <p className="font-semibold text-sm sm:text-base lg:text-lg">
                              Smart Dashboard
                            </p>
                          </a>
                        </li>
                        
                        {/* Personalization */}
                        <li>
                          <div className="space-y-1 sm:space-y-2">
                            <div className="flex gap-2 sm:gap-4 items-center">
                              <div className="w-6 sm:w-8 flex items-center">
                                <img
                                  src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/personalize_optuxv.svg"
                                  alt="Personalization"
                                  className="w-6 sm:w-8"
                                />
                              </div>
                              <p className="font-semibold text-sm sm:text-base lg:text-lg">
                                Personalization
                              </p>
                            </div>
                            <ul className="text-gray-600 text-xs sm:text-sm lg:text-base space-y-1 sm:space-y-2 ml-8 sm:ml-12">
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/personalization/design/">Design</a>
                              </li>
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/personalization/language/">Language</a>
                              </li>
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/personalization/white-labelled-apps/">White labelled apps</a>
                              </li>
                            </ul>
                          </div>
                        </li>
                        
                        {/* User Management */}
                        <li>
                          <div className="space-y-1 sm:space-y-2">
                            <div className="flex gap-2 sm:gap-4 items-center">
                              <div className="w-6 sm:w-8 flex items-center">
                                <img
                                  src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/Operations_team_Icon_1_dkmxg3.svg"
                                  alt="User Management"
                                  className="w-6 sm:w-8"
                                />
                              </div>
                              <p className="font-semibold text-sm sm:text-base lg:text-lg">
                                User Management
                              </p>
                            </div>
                            <ul className="text-gray-600 text-xs sm:text-sm lg:text-base space-y-1 sm:space-y-2 ml-8 sm:ml-12">
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/user-management/profile-management/">Profile Management</a>
                              </li>
                              <li className="hover:text-yellow-500 cursor-pointer">
                                <a href="https://senpiper.com//features/user-management/rbac/">Role Based Access Control</a>
                              </li>
                            </ul>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Industries Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setIsIndustriesOpen(true)}
              onMouseLeave={() => setIsIndustriesOpen(false)}
            >
              <div className={getNavClass("")}>
                Industries
              </div>
              
              {/* Dropdown Content */}
              {isIndustriesOpen && (
                <div className="absolute top-full -left-8 sm:-left-16 bg-white text-gray-800 group-hover:visible w-64 sm:w-80 lg:w-96 p-4 sm:p-6 rounded-lg shadow-xl border border-gray-200 space-y-4 sm:space-y-6">
                  {/* By Industry */}
                  <div>
                    <div className="flex gap-2 sm:gap-4 items-center">
                      <div className="w-6 sm:w-8 flex items-center">
                        <img
                          src="https://res.cloudinary.com/senpiper/image/upload/v1680243303/senpiper_assets/new_website_assets/Enterprise_wlqr2f.svg"
                          alt="By Industry"
                          className="w-6 sm:w-8"
                        />
                      </div>
                      <p className="font-semibold text-sm sm:text-base lg:text-lg">
                        By Industry
                      </p>
                    </div>
                    <ul className="text-gray-600 text-xs sm:text-sm lg:text-base space-y-1 sm:space-y-2 ml-8 sm:ml-12">
                      <li className="hover:text-yellow-500 cursor-pointer">
                        <a href="https://senpiper.com//products/by-industry/manufacturing/">Manufacturing</a>
                      </li>
                      <li className="hover:text-yellow-500 cursor-pointer">
                        <a href="https://senpiper.com//products/by-industry/warehouse-and-logistics/">Warehouse and logistics</a>
                      </li>
                      <li className="hover:text-yellow-500 cursor-pointer">
                        <a href="https://senpiper.com//products/by-industry/facility-management/">Facility management</a>
                      </li>
                      <li className="hover:text-yellow-500 cursor-pointer">
                        <a href="https://senpiper.com//products/by-industry/eGovernance/">e-Governance</a>
                      </li>
                      <li className="hover:text-yellow-500 cursor-pointer">
                        <a href="https://senpiper.com//products/by-industry/information-technology/">Information technology</a>
                      </li>
                    </ul>
                  </div>
                  
                  {/* By Role */}
                  <div>
                    <div className="flex gap-2 sm:gap-4 items-center">
                      <div className="w-6 sm:w-8 flex items-center">
                        <img
                          src="https://res.cloudinary.com/senpiper/image/upload/v1680243329/senpiper_assets/new_website_assets/Support_icon_dc5czp.svg"
                          alt="By Role"
                          className="w-6 sm:w-8"
                        />
                      </div>
                      <p className="font-semibold text-sm sm:text-base lg:text-lg">
                        By Role
                      </p>
                    </div>
                    <ul className="text-gray-600 text-xs sm:text-sm lg:text-base space-y-1 sm:space-y-2 ml-8 sm:ml-12">
                      <li className="hover:text-yellow-500 cursor-pointer">
                        <a href="https://senpiper.com//products/by-role/it-head/">IT Head</a>
                      </li>
                      <li className="hover:text-yellow-500 cursor-pointer">
                        <a href="https://senpiper.com//products/by-role/business-manager/">Business manager</a>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://senpiper.com/AboutUs/"
              target="_blank"
              rel="noopener noreferrer"
              className={getNavClass("")} 
            >
              About Us
            </a>

            {/* Project Highlight */}
            <a href="/caseStudies-detail" className={getNavClass("/caseStudies-detail")}>
              Project Highlight
            </a>
          </div>

          {/* Desktop Contact Button */}
          <button
            onClick={() => setIsContactOpen(true)}
            className="hidden sm:block bg-[#102E7A] text-white text-sm sm:text-base lg:text-lg xl:text-xl font-semibold px-3 sm:px-4 lg:px-6 py-1.5 sm:py-2 rounded hover:bg-[#0d2563] transition-colors font-['Open Sans']"
          >
            Contact Us
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="sm:hidden flex flex-col justify-center items-center w-8 h-8 space-y-1"
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span
              className={`block w-6 h-0.5 bg-gray-600 transition-transform duration-300 ${
                isMenuOpen ? "rotate-45 translate-y-1.5" : ""
              }`}
            ></span>
            <span
              className={`block w-6 h-0.5 bg-gray-600 transition-opacity duration-300 ${
                isMenuOpen ? "opacity-0" : ""
              }`}
            ></span>
            <span
              className={`block w-6 h-0.5 bg-gray-600 transition-transform duration-300 ${
                isMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
              }`}
            ></span>
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={`sm:hidden absolute top-full left-0 w-full bg-white shadow-lg transition-all duration-300 ease-in-out ${
            isMenuOpen
              ? "opacity-100 visible max-h-screen"
              : "opacity-0 invisible max-h-0"
          } overflow-y-auto`}
        >
          <div className="px-4 py-3 space-y-2">
            {/* Home */}
            <a
              href="https://senpiper.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMenuOpen(false)}
              className="block text-gray-700 text-base font-medium hover:text-[#102E7A] transition-colors py-2 font-['IBM Plex Sans']"
            >
              Home
            </a>
            
            {/* Mobile Low-code Platform Submenu with Expand Button */}
            <div className="space-y-1 pb-2">
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-700 text-base font-medium font-['IBM Plex Sans']">
                  Low-code Platform
                </span>
                <button
                  onClick={() => toggleExpandedMenu('lowCode')}
                  className="text-gray-500 hover:text-[#102E7A] transition-colors p-1"
                  aria-label={expandedMenus.lowCode ? "Collapse menu" : "Expand menu"}
                >
                  <svg
                    className={`w-5 h-5 transform transition-transform duration-200 ${
                      expandedMenus.lowCode ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              {/* Expandable Content */}
              {expandedMenus.lowCode && (
                <div className="ml-3 space-y-3 pl-3 mt-2 border-l-2 border-gray-100">
                  {/* Low Code Section */}
                  <div className="space-y-2">
                    <div className="flex gap-2 items-center text-gray-700">
                      <img
                        src="https://res.cloudinary.com/senpiper/image/upload/v1680631485/senpiper_assets/new_website_assets/Flexible_Integration_icon_h9v77v.svg"
                        alt="Low-Code"
                        className="w-5 h-5"
                      />
                      <span className="font-semibold text-sm tracking-wide text-gray-600">Low-Code</span>
                    </div>
                    <ul className="ml-6 space-y-1.5 text-gray-500">
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//platform/" onClick={() => setIsMenuOpen(false)} className="text-sm py-1 block">Our Platform</a>
                      </li>
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//products/low-code/low-code/" onClick={() => setIsMenuOpen(false)} className="text-sm py-1 block">What is Low-code?</a>
                      </li>
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//products/low-code/comparison/" onClick={() => setIsMenuOpen(false)} className="text-sm py-1 block">Low-code comparison</a>
                      </li>
                    </ul>
                  </div>

                  {/* Features Sections - Complete */}
                  <div className="space-y-3 pt-1">
                    {/* Data Capture */}
                    <div className="space-y-1.5">
                      <div className="flex gap-2 items-center text-gray-700">
                        <img
                          src="https://res.cloudinary.com/senpiper/image/upload/v1666804563/senpiper_assets/new_website_assets/data_capture_uz7hxc.svg"
                          alt="Data Capture"
                          className="w-5 h-5"
                        />
                        <span className="text-sm font-medium">Data Capture</span>
                      </div>
                      <ul className="ml-6 space-y-1 text-gray-500">
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/data-capture/data-collection/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Data Collection</a>
                        </li>
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/data-capture/form-field/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Form Fields</a>
                        </li>
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/data-capture/schema-relationship/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Schema Relation</a>
                        </li>
                      </ul>
                    </div>

                    {/* Automation */}
                    <div className="space-y-1.5">
                      <div className="flex gap-2 items-center text-gray-700">
                        <img
                          src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/Automation_w3lto6.svg"
                          alt="Automation"
                          className="w-5 h-5"
                        />
                        <span className="text-sm font-medium">Automation</span>
                      </div>
                      <ul className="ml-6 space-y-1 text-gray-500">
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/automation/workflow/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Workflow</a>
                        </li>
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/automation/scheduler/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Scheduler</a>
                        </li>
                      </ul>
                    </div>

                    {/* Communication */}
                    <a href="https://senpiper.com//features/communication/" onClick={() => setIsMenuOpen(false)} className="flex gap-2 items-center text-gray-700 hover:text-yellow-500 transition-colors py-1">
                      <img
                        src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/Chat_lflyjy.svg"
                        alt="Communication"
                        className="w-5 h-5"
                      />
                      <span className="text-sm font-medium">Communication</span>
                    </a>

                    {/* Security */}
                    <a href="https://senpiper.com//features/security/" onClick={() => setIsMenuOpen(false)} className="flex gap-2 items-center text-gray-700 hover:text-yellow-500 transition-colors py-1">
                      <img
                        src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/security_gakizz.svg"
                        alt="Security"
                        className="w-5 h-5"
                      />
                      <span className="text-sm font-medium">Security</span>
                    </a>

                    {/* Smart Dashboard */}
                    <a href="https://senpiper.com//features/analytics/" onClick={() => setIsMenuOpen(false)} className="flex gap-2 items-center text-gray-700 hover:text-yellow-500 transition-colors py-1">
                      <img
                        src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/analytis_irmqhy.svg"
                        alt="Smart Dashboard"
                        className="w-5 h-5"
                      />
                      <span className="text-sm font-medium">Smart Dashboard</span>
                    </a>

                    {/* Personalization */}
                    <div className="space-y-1.5">
                      <div className="flex gap-2 items-center text-gray-700">
                        <img
                          src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/personalize_optuxv.svg"
                          alt="Personalization"
                          className="w-5 h-5"
                        />
                        <span className="text-sm font-medium">Personalization</span>
                      </div>
                      <ul className="ml-6 space-y-1 text-gray-500">
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/personalization/design/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Design</a>
                        </li>
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/personalization/language/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Language</a>
                        </li>
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/personalization/white-labelled-apps/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">White labelled apps</a>
                        </li>
                      </ul>
                    </div>

                    {/* User Management */}
                    <div className="space-y-1.5">
                      <div className="flex gap-2 items-center text-gray-700">
                        <img
                          src="https://res.cloudinary.com/senpiper/image/upload/v1666804605/senpiper_assets/new_website_assets/Operations_team_Icon_1_dkmxg3.svg"
                          alt="User Management"
                          className="w-5 h-5"
                        />
                        <span className="text-sm font-medium">User Management</span>
                      </div>
                      <ul className="ml-6 space-y-1 text-gray-500">
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/user-management/profile-management/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Profile Management</a>
                        </li>
                        <li className="hover:text-yellow-500 transition-colors">
                          <a href="https://senpiper.com//features/user-management/rbac/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Role Based Access Control</a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Industries Submenu with Expand Button */}
            <div className="space-y-1 pb-2">
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-700 text-base font-medium font-['IBM Plex Sans']">
                  Industries
                </span>
                <button
                  onClick={() => toggleExpandedMenu('industries')}
                  className="text-gray-500 hover:text-[#102E7A] transition-colors p-1"
                  aria-label={expandedMenus.industries ? "Collapse menu" : "Expand menu"}
                >
                  <svg
                    className={`w-5 h-5 transform transition-transform duration-200 ${
                      expandedMenus.industries ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              {/* Expandable Content */}
              {expandedMenus.industries && (
                <div className="ml-3 space-y-3 pl-3 mt-2 border-l-2 border-gray-100">
                  {/* By Industry */}
                  <div className="space-y-1.5">
                    <div className="flex gap-2 items-center text-gray-700">
                      <img
                        src="https://res.cloudinary.com/senpiper/image/upload/v1680243303/senpiper_assets/new_website_assets/Enterprise_wlqr2f.svg"
                        alt="By Industry"
                        className="w-5 h-5"
                      />
                      <span className="font-semibold text-sm tracking-wide text-gray-600">By Industry</span>
                    </div>
                    <ul className="ml-6 space-y-1 text-gray-500">
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//products/by-industry/manufacturing/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Manufacturing</a>
                      </li>
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//products/by-industry/warehouse-and-logistics/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Warehouse and logistics</a>
                      </li>
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//products/by-industry/facility-management/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Facility management</a>
                      </li>
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//products/by-industry/eGovernance/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">e-Governance</a>
                      </li>
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//products/by-industry/information-technology/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Information technology</a>
                      </li>
                    </ul>
                  </div>

                  {/* By Role */}
                  <div className="space-y-1.5">
                    <div className="flex gap-2 items-center text-gray-700">
                      <img
                        src="https://res.cloudinary.com/senpiper/image/upload/v1680243329/senpiper_assets/new_website_assets/Support_icon_dc5czp.svg"
                        alt="By Role"
                        className="w-5 h-5"
                      />
                      <span className="font-semibold text-sm tracking-wide text-gray-600">By Role</span>
                    </div>
                    <ul className="ml-6 space-y-1 text-gray-500">
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//products/by-role/it-head/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">IT Head</a>
                      </li>
                      <li className="hover:text-yellow-500 transition-colors">
                        <a href="https://senpiper.com//products/by-role/business-manager/" onClick={() => setIsMenuOpen(false)} className="text-sm py-0.5 block">Business manager</a>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* About Us */}
            <a
              href="https://senpiper.com/AboutUs/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMenuOpen(false)}
              className="block text-gray-700 text-base font-medium hover:text-[#102E7A] transition-colors py-2 font-['IBM Plex Sans']"
            >
              About Us
            </a>

            {/* Project Highlight */}
            <a
              href="/caseStudies-detail"
              onClick={() => setIsMenuOpen(false)}
              className="block text-gray-700 text-base font-medium hover:text-[#102E7A] transition-colors py-2 font-['IBM Plex Sans']"
            >
              Project Highlight
            </a>

            {/* Contact Us Text Button */}
            <button
              onClick={() => {
                setIsMenuOpen(false);
                setIsContactOpen(true);
              }}
              className="block text-gray-700 text-base font-medium hover:text-[#102E7A] transition-colors py-2 font-['IBM Plex Sans'] w-full text-left"
            >
              Contact Us
            </button>

            {/* Contact Us CTA Button */}
            <div className="pt-2">
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsContactOpen(true);
                }}
                className="block w-full bg-[#102E7A] text-white text-base font-semibold px-4 py-2.5 rounded text-center hover:bg-[#0d2563] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-['IBM Plex Sans']"
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Contact Modal */}
      <ContactModal
        open={isContactOpen}
        onClose={() => setIsContactOpen(false)}
        onSave={(contact) => {
          console.log("Contact saved:", contact);
        }}
      />
    </>
  );
};

export default Navbar;